import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Paper, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LanIcon from '@mui/icons-material/Lan';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../../animations/variants';
import {
  CK, FONT_BODY, FONT_MONO, ResourceTree, actionRowSx, buildResourceTree,
  dialogProps, dialogTitleSx, insetPanelSx, listCardSx, sectionCardSx, statusChipSx,
} from '../../portalStyles';
import PortalPageHeader from '../../../../components/portal/PortalPageHeader';
import {
  PortalEmptyState, PortalErrorState, PortalLoading,
} from '../../../../components/portal/PortalStates';
import {
  createIpamPool, getIpamConfig, getIpamPoolTree, listIpamScopes,
} from '../../../../services/ipamApi';
import { IpamStateChip, ipamStateIsIsolated } from './ipamState';
import {
  IPAM_POOLS_CONFIG_UNREAD_ERROR, IPAM_POOLS_EMPTY_DESCRIPTION,
  IPAM_POOLS_LIVENESS_UNREAD_BODY, IPAM_POOLS_LIVENESS_UNREAD_TITLE, IPAM_POOLS_READ_FAILED,
  IPAM_POOLS_SUBTITLE, IPAM_POOLS_UNVERIFIED_TREE_BODY, IPAM_POOLS_UNVERIFIED_TREE_TITLE,
  IPAM_SCOPE_DEAD_OPTIONS_BODY, IPAM_SCOPE_DEAD_OPTIONS_TITLE,
  ipamConfigNoteSeverity, ipamConfigNoteTitle, ipamCreatePoolDisabledReason,
  ipamResidualIpamCard, ipamScopeCreateBlock, ipamUnscopedAlert, ipamUnscopedRootChip,
} from './ipamCopy';

// VPC IPAM — the pool hierarchy.
//
// ── THIS IS THE ONE NON-FLAT PAGE IN THE PORTAL ─────────────────────────────
//
// Every other table in Org Services is a list. Address space is a hierarchy by
// nature — scope → top-level pool → regional pool → nested pool, up to AWS's
// depth ceiling — and the whole value of the screen is comparing a pool against
// the pool it was carved out of. That comparison is impossible in a flat list
// with a "parent" column, so this renders through <ResourceTree>: ONE real
// <Table> with computed indentation, never nested tables, because nested tables
// break column alignment across depths.
//
// THE TREE IS NEVER RECOMPUTED HERE. `parentPoolId`, `depth` and `ledgerPath`
// are materialised server-side at create and refresh time from AWS's
// SourceIpamPoolId / PoolDepth. Re-deriving the hierarchy in the browser would
// need the scope, and describe-ipam-pools DOES NOT REPORT THE SCOPE AT ALL —
// it answers with a null scope id even to the pool's own owner. This page only
// re-nests rows the server already placed.
//
// ── WHY THIS PAGE HAS NO COLD-START STATE, AND MUST NOT GROW ONE ────────────
//
// Four IPAM screens (Dashboard, Address Space, Allocations, Public IPs) render
// <IpamDiscoveryColdStart> when they are empty, because their rows come from
// AWS's periodic discovery sweep and "enabled, zero rows" is the normal first
// twenty minutes.
//
// POOLS ARE NOT DISCOVERED. They are created — by Prism, or by the customer and
// then adopted. Telling an operator to wait for a discovery sweep here tells them
// to wait for something that is never going to arrive: no sweep will ever invent
// a pool. Do not import IpamDiscoveryColdStart into this file.
//
// BUT AN EMPTY TREE IS STILL NOT PROOF THERE ARE NONE, and this page said it was.
// `refreshPoolLedger` RE-READS the pools from AWS on every refresh and ADOPTS
// ones Prism did not create; when that read fails only a job note records it, so
// an empty tree is equally "you have no pools" and "the pool pass did not land".
// Both the page subtitle and the empty state now say which of the two they can
// actually establish — see IPAM_POOLS_SUBTITLE.
//
// ── AND IT HAS A NOT-LIVE STATE, AS EVERY SIBLING DOES ──────────────────────
//
// A config row is not an IPAM: one exists from `awaiting_consent` onward and a
// `failed` row persists forever, while the sidebar renders this link in every
// state. Without the branch below an unconfigured, abandoned, failed, blocked or
// disabled tenant got "No pools yet" and a Create pool button whose dialog opens
// with an EMPTY Scope select — AWS creates the default private scope with the
// IPAM, so there is nothing to choose — leaving `canSubmit` permanently false and
// the operator staring at a greyed button that explains nothing.
//
// ── THE UNSCOPED ROOT ───────────────────────────────────────────────────────
//
// A pool whose scope does not resolve is rendered under a synthetic "Unscoped"
// root with a warning chip, NEVER dropped. A silently missing row in a hierarchy
// view is indistinguishable from "you do not have that pool" — which, for a pool
// that exists in AWS and is occupying quota, is the worst thing this screen
// could say.

// Slate, per the capability's accent. Duplicated per page file by convention —
// there is no ipamStyles.js and the pages that would share one are owned by
// different lanes.
const IPAM_COLOR = '#455A64';

// Synthetic node ids. Scope ids are prefixed so they cannot collide with a pool
// id in the same `expanded` set; POOL NODES ARE KEYED BY THEIR RAW AWS ID
// (`ipam-pool-…`) precisely so `?expand=ipam-pool-0123…` is a URL an operator
// can paste from anywhere.
const SCOPE_NODE_PREFIX = 'ipam-scope-node:';
const UNSCOPED_NODE_ID = 'ipam-unscoped-root';
const scopeNodeId = (scopeId) => `${SCOPE_NODE_PREFIX}${scopeId}`;

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : []);

// OperatingRegions is a `text` column holding JSON []string, so it arrives as a
// STRING and not an array. Parsed defensively: a locale dropdown that throws
// takes the whole create dialog down with it.
const parseRegions = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((r) => typeof r === 'string') : [];
  } catch {
    return [];
  }
};

const isPublicScope = (scope) => String(scope?.scopeType || '').toLowerCase() === 'public';

// Who owns the row, as one chip. `adopted` and `awsCreated` are Prism-local
// facts about OBLIGATION, not about AWS state, and they decide whether Prism
// will ever delete the thing — so they are on the row rather than in a tooltip.
const ownershipChip = (row) => {
  if (row?.awsCreated) {
    return { intent: 'neutral', label: 'AWS-created', hint: 'AWS created this inside the IPAM. Prism never deletes it on its own.' };
  }
  if (row?.adopted) {
    return { intent: 'purple', label: 'Adopted', hint: 'The customer created this and Prism adopted it. Prism will never delete it — doing so would destroy allocation history that is the customer’s.' };
  }
  if (row?.prismManaged) {
    return { intent: 'info', label: 'Prism', hint: 'Prism created this and will remove it during teardown.' };
  }
  return { intent: 'neutral', label: 'Unmanaged', hint: 'Prism has no ownership record for this row.' };
};

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// `enabled` and `partial_failure` are the ONLY two statuses at which an IPAM
// exists. Deliberately the same predicate as every sibling page rather than a
// second, looser opinion about the same question.
const ipamIsLive = (status) => status === 'enabled' || status === 'partial_failure';

// One card per non-live status, because each has a different way out and none of
// them is "create a pool". Shape and wording follow IpamAllocations.js's
// NOT_LIVE_META so the seven IPAM screens answer the same question the same way.
const NOT_LIVE_META = {
  awaiting_consent: {
    title: 'VPC IPAM setup was never finished',
    description: 'Setup was started but stopped at the cost estimate, so no IPAM and no scopes were '
      + 'ever created. A pool has to live inside a scope, and there is none — finish the setup '
      + 'wizard first.',
  },
  in_progress: {
    title: 'VPC IPAM is still being set up',
    description: 'Prism is still creating the IPAM and its default scopes. There is nothing to '
      + 'carve a pool out of until that finishes.',
  },
  failed: {
    title: 'VPC IPAM setup failed',
    description: 'Setup did not finish, so there is no IPAM and no scope to create a pool in. '
      + 'Nothing is running and nothing will resume on its own — re-run the setup wizard.',
  },
  blocked: {
    title: 'A VPC IPAM teardown is waiting on you',
    description: 'A teardown stopped part-way, so this IPAM is mid-removal and its pools are being '
      + 'drained rather than added to. The IPAM is still there and still billing until it is dealt '
      + 'with — clear the teardown in Settings first.',
  },
  disabled: {
    title: 'VPC IPAM is not enabled yet',
    description: 'There is no IPAM, so there are no scopes and nothing to carve a pool out of. '
      + 'Enable VPC IPAM to start planning your address space.',
  },
};

const notLiveMeta = (status) => NOT_LIVE_META[status] || NOT_LIVE_META.disabled;

// --- LIVENESS, ASKED BEFORE THE ROWS ARE, WITH THREE ANSWERS -----------------
//
// THIS USED TO BE A BOOLEAN CONSULTED INSIDE `if (pools.length === 0)`, AND THAT
// ORDERING WAS THE BUG. A tenant whose IPAM had been torn down was served two
// stale pools by the ledger, and because the tree was NOT empty the not-live
// question was never reached: the page drew the rows under the ordinary chrome,
// "Creating", "Managed by Prism", as though the capability were running. The
// contents of the tree cannot license a claim about whether an IPAM exists —
// only the config row can — so the question is now asked first and the rows are
// consulted afterwards.
//
// The three answers, and why the third is not padding:
//
//   LIVE      — `enabled` or `partial_failure` (ipamIsLive), or one of the two
//               statuses that can sit over a real IPAM (`in_progress`,
//               `blocked`) WITH a sweep already landed. `partial_failure` is a
//               LIVE state: an enable that partly failed still has a real IPAM
//               and real pools, and hiding them would be its own false negative.
//               This is why the test is ipamIsLive and never `=== 'enabled'`.
//   NOT_LIVE  — a status Prism read that means no IPAM exists, or NO CONFIG ROW
//               ON A SUCCESSFUL READ, which is the config route's documented
//               answer for a tenant that never configured IPAM (200 + data:null)
//               and therefore a real negative.
//   UNREAD    — Prism could not find out. A read that failed or has not landed,
//               or a config row carrying no status at all. "Do not know" must
//               not be spent as "not set up": that sentence points an operator
//               whose IPAM is running and billing at the setup wizard.
//
// `in_progress` and `blocked` are judged on whether a sweep has ever landed.
// Every other status is judged on the status alone, deliberately ignoring
// firstDiscoveryAt — it "IS SET ONCE AND NEVER RESET", so a tenant who tore an
// IPAM down and restarted the wizard still carries the old one.
const LIVE = 'live';
const NOT_LIVE = 'not_live';
const UNREAD = 'unread';

const ipamLiveness = (config, configRead) => {
  if (config) {
    if (ipamIsLive(config.status)) return LIVE;
    // A config row carrying no status at all is not a decided negative either —
    // the same rule, one level down. Without this arm NOT_LIVE_META's `disabled`
    // fallback dresses a blank as "VPC IPAM is not enabled yet", which is a
    // verdict invented out of a field Prism never read.
    if (!config.status) return UNREAD;
    if (config.status === 'in_progress' || config.status === 'blocked') {
      return config.firstDiscoveryAt ? LIVE : NOT_LIVE;
    }
    return NOT_LIVE;
  }
  if (configRead === 'ok') return NOT_LIVE;
  return UNREAD;
};

export default function IpamPools() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pools, setPools] = useState(null);
  const [scopes, setScopes] = useState([]);
  const [config, setConfig] = useState(null);
  // The config read's OWN outcome, tracked apart from `error`, because `config`
  // being null is two different facts: a tenant that never configured IPAM (a
  // real answer) and a read that did not land (no answer at all). Same three
  // values IpamAllocations.js, IpamAccounts.js and IpamPublicIPs.js use.
  const [configRead, setConfigRead] = useState('pending');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // null means "the operator has not touched the tree yet", which is what lets
  // the default expansion be DERIVED from the data (and from the URL) instead of
  // being written into state by an effect that would then clobber a toggle.
  const [expandedOverride, setExpandedOverride] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      // Three independent reads. `listIpamScopes` and `getIpamPoolTree` both
      // answer an EMPTY PAGE rather than an error for an unconfigured tenant, so
      // neither can fail this page into the red card; `getIpamConfig` answers
      // data:null for the same tenant, which is a real answer meaning "nothing
      // yet" and not a fault.
      // The config read commits ITS OWN result and ITS OWN outcome, rather than
      // being destructured out of the settled Promise.all. A tree read that
      // rejects would otherwise discard a config Prism successfully read and
      // downgrade a known status to "unknown" — and the whole point of the
      // liveness gate is that the config row is the only thing licensed to
      // answer it.
      const [treeRes, scopeRes] = await Promise.all([
        getIpamPoolTree(),
        listIpamScopes(),
        getIpamConfig().then(
          (cfgRes) => { setConfig(cfgRes?.data ?? null); setConfigRead('ok'); },
          (e) => { setConfigRead('failed'); throw e; },
        ),
      ]);
      setPools(rowsOf(treeRes));
      setScopes(rowsOf(scopeRes));
      setError(null);
    } catch (e) {
      // NEVER `e.message` alone. A rejection carrying an empty message left
      // `error` falsy, and every branch downstream — the not-live card, the
      // "No pools yet" zero — is a confident claim about a customer's setup
      // that would then be made out of a read that never landed.
      setError(e?.message || IPAM_POOLS_READ_FAILED);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const poolById = useMemo(() => {
    const map = new Map();
    (pools || []).forEach((p) => map.set(p.poolId, p));
    return map;
  }, [pools]);

  // A pool hangs off its SOURCE POOL when nested, its SCOPE when top-level, and
  // the synthetic Unscoped root when neither resolves.
  const parentNodeIdOf = useCallback((pool) => {
    if (pool.parentPoolId) return pool.parentPoolId;
    if (pool.unscoped || !pool.scopeId) return UNSCOPED_NODE_ID;
    return scopeNodeId(pool.scopeId);
  }, []);

  const nodes = useMemo(() => {
    const items = [
      ...scopes.map((scope) => ({
        kind: 'scope',
        nodeId: scopeNodeId(scope.scopeId),
        parentNodeId: null,
        scope,
      })),
      ...(pools || []).map((pool) => ({
        kind: 'pool',
        nodeId: pool.poolId,
        parentNodeId: parentNodeIdOf(pool),
        pool,
      })),
    ];
    return buildResourceTree(items, {
      getId: (item) => item.nodeId,
      getParentId: (item) => item.parentNodeId,
      // Supplied unconditionally: buildResourceTree only materialises it when
      // something actually failed to place, so there is no empty "Unscoped" root
      // on a healthy tenant.
      orphanRoot: { kind: 'unscoped', nodeId: UNSCOPED_NODE_ID, parentNodeId: null },
    });
  }, [pools, scopes, parentNodeIdOf]);

  // Everything between a deep-linked pool and its root, so `?expand=ipam-pool-x`
  // opens the BRANCH rather than setting a flag on a row nobody can see.
  const ancestorsOf = useCallback((poolId) => {
    const chain = [];
    const seen = new Set();
    let current = poolById.get(poolId);
    while (current && !seen.has(current.poolId)) {
      seen.add(current.poolId);
      chain.push(current.poolId);
      if (current.parentPoolId) {
        current = poolById.get(current.parentPoolId);
        continue;
      }
      chain.push(
        current.unscoped || !current.scopeId ? UNSCOPED_NODE_ID : scopeNodeId(current.scopeId)
      );
      current = null;
    }
    return chain;
  }, [poolById]);

  const expandParam = searchParams.get('expand') || '';

  // Scopes open by default — a tree whose every root is collapsed shows nothing
  // and reads as an empty page. The URL then only ever has to carry POOL ids,
  // which keeps a deep link short enough to paste into a ticket.
  const defaultExpanded = useMemo(() => {
    const set = new Set(scopes.map((s) => scopeNodeId(s.scopeId)));
    set.add(UNSCOPED_NODE_ID);
    expandParam
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((id) => {
        set.add(id);
        ancestorsOf(id).forEach((a) => set.add(a));
      });
    return set;
  }, [scopes, expandParam, ancestorsOf]);

  const expanded = expandedOverride || defaultExpanded;

  const handleToggle = useCallback((id, next) => {
    const updated = new Set(expanded);
    if (next) updated.add(id); else updated.delete(id);
    setExpandedOverride(updated);

    // Keep the URL an honest deep link. Only pool ids go in: the synthetic scope
    // and Unscoped ids are open by default anyway, and putting them in the query
    // string would bloat it without changing what the reader sees.
    const params = new URLSearchParams(searchParams);
    const poolIds = [...updated].filter((nodeId) => poolById.has(nodeId));
    if (poolIds.length > 0) params.set('expand', poolIds.join(','));
    else params.delete('expand');
    setSearchParams(params, { replace: true });
  }, [expanded, poolById, searchParams, setSearchParams]);

  const unscopedCount = useMemo(
    () => (pools || []).filter((p) => p.unscoped || !p.scopeId).length,
    [pools]
  );
  const isolatedCount = useMemo(
    () => (pools || []).filter((p) => ipamStateIsIsolated(p.state)).length,
    [pools]
  );
  const missingCount = useMemo(
    () => (pools || []).filter((p) => p.observedMissing).length,
    [pools]
  );

  // WHY CREATE POOL IS OFF — and it used to be off for no stated reason at all.
  // The dialog's Scope select is fed from listIpamScopes, which answers an EMPTY
  // PAGE for a tenant with no IPAM; with no scope to pick, `canSubmit` can never
  // become true. A dead submit behind a live-looking button is the defect, so the
  // button itself goes off and carries the reason.
  const privateScopes = useMemo(() => scopes.filter((s) => !isPublicScope(s)), [scopes]);
  // AND HOW MANY OF THOSE CAN ACTUALLY BE CREATED INTO. A dead scope is still
  // listed — see ipamScopeCreateBlock — so `privateScopes.length` counts rows
  // that would refuse the create, and the button would come up live on a tenant
  // whose every scope belongs to an IPAM that no longer exists.
  const creatableScopes = useMemo(
    () => privateScopes.filter((s) => !ipamScopeCreateBlock(s)),
    [privateScopes]
  );
  const unscopedAlert = useMemo(
    () => ipamUnscopedAlert({ count: unscopedCount, scopeCount: scopes.length }),
    [unscopedCount, scopes.length]
  );
  const liveness = ipamLiveness(config, configRead);
  // Three arms, and the UNREAD one deliberately falls through to the scope rule
  // rather than inventing a refusal. Prism does not know the capability is dead;
  // the service is the authority on that and CreatePool refuses a non-live IPAM
  // with a real sentence, whereas a greyed button asserting "there is no IPAM"
  // out of an unread config states something Prism has not established. If there
  // is genuinely no private scope to create into, that arm still fires — that is
  // a fact about the scopes Prism DID read.
  //
  // AND IT ASKS ABOUT THE IPAM, NOT ABOUT THE STATUS. notLiveMeta's `disabled`
  // arm says "There is no IPAM", which is false over an ADOPTED IPAM a teardown
  // left running — see ipamResidualIpamCard. The tooltip is the reason the button
  // is off, so it must not be the one sentence on the page still claiming the
  // customer owns nothing.
  const residualCard = ipamResidualIpamCard(config, 'pools');
  const noteSeverity = ipamConfigNoteSeverity(config?.note, config?.noteSource);
  const noteTitle = ipamConfigNoteTitle(config?.note, config?.noteSource);
  const createDisabledReason = liveness === NOT_LIVE
    ? (residualCard || notLiveMeta(config?.status)).description
    : ipamCreatePoolDisabledReason({
      privateScopeCount: creatableScopes.length,
      deadPrivateScopeCount: privateScopes.length - creatableScopes.length,
    });

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'Scope / pool',
      width: '34%',
      render: (node) => {
        if (node.kind === 'unscoped') {
          // TWO KINDS OF UNPLACED, NOT ONE. "No scope resolved" describes a
          // lookup that missed, which is right only while OTHER pools did place
          // under a scope. With no scopes held at all there was nothing to
          // resolve against, and the row is far more likely to be left over from
          // an IPAM that is gone — a hiccup label on a tombstone.
          const unscopedChip = ipamUnscopedRootChip({ scopeCount: scopes.length });
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem' }}>
                Unscoped
              </Typography>
              <Tooltip title={unscopedChip.hint}>
                <Chip size="small" icon={<WarningAmberIcon />} label={unscopedChip.label} sx={statusChipSx('warning')} />
              </Tooltip>
            </Box>
          );
        }
        if (node.kind === 'scope') {
          const { scope } = node;
          return (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem' }}>
                  {scope.description || (isPublicScope(scope) ? 'Public scope' : 'Private scope')}
                </Typography>
                <Chip
                  size="small"
                  label={isPublicScope(scope) ? 'Public' : 'Private'}
                  sx={statusChipSx(isPublicScope(scope) ? 'warning' : 'neutral')}
                />
                {scope.isDefault && (
                  <Tooltip title="AWS created this scope with the IPAM. It cannot be deleted on its own.">
                    <Chip size="small" label="Default" sx={statusChipSx('neutral')} />
                  </Tooltip>
                )}
                {/* THE ROW STAYS. Only the create target goes. The same chip the
                    pool rows carry, so the fact the Create pool dialog acts on is
                    visible on the tree that names the scope. */}
                {scope.observedMissing && (
                  <Tooltip title="AWS stopped returning this scope but Prism has not deleted it. It is either already gone (harmless) or an orphan that still exists and still occupies quota — which is the case that matters. Prism will not create a pool into it: it cannot confirm the scope is even private, and one pool in a public scope makes the IPAM permanently undeletable.">
                    <Chip size="small" icon={<WarningAmberIcon />} label="Not seen in AWS" sx={statusChipSx('warning')} />
                  </Tooltip>
                )}
              </Box>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: CK.muted }}>
                {scope.scopeId}
              </Typography>
            </Box>
          );
        }
        const { pool } = node;
        return (
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem' }}>
                {pool.description || 'Pool'}
              </Typography>
              {pool.observedMissing && (
                <Tooltip title="AWS stopped returning this pool but Prism has not deleted it. It is either already gone (harmless) or an orphan that is still occupying quota and still needs naming — which is the case that matters.">
                  <Chip size="small" icon={<WarningAmberIcon />} label="Not seen in AWS" sx={statusChipSx('warning')} />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: CK.muted }}>
              {pool.poolId}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'family',
      label: 'Family',
      render: (node) =>
        node.kind === 'pool'
          ? <Box component="span" sx={{ fontFamily: FONT_MONO }}>{dash(node.pool.addressFamily)}</Box>
          : <Box component="span" sx={{ color: CK.muted }}>—</Box>,
    },
    {
      key: 'locale',
      label: 'Locale',
      render: (node) => {
        if (node.kind !== 'pool') return <Box component="span" sx={{ color: CK.muted }}>—</Box>;
        if (!node.pool.locale) {
          return (
            <Tooltip title="This pool has no locale. AWS cannot allocate its CIDRs to a VPC in any region until one is set, and a locale cannot be changed after the pool is created.">
              <Box component="span" sx={{ color: CK.muted, fontFamily: FONT_BODY }}>None</Box>
            </Tooltip>
          );
        }
        return <Box component="span" sx={{ fontFamily: FONT_MONO }}>{node.pool.locale}</Box>;
      },
    },
    {
      key: 'state',
      label: 'State',
      render: (node) =>
        node.kind === 'unscoped'
          ? <Box component="span" sx={{ color: CK.muted }}>—</Box>
          : <IpamStateChip state={node.kind === 'scope' ? node.scope.state : node.pool.state} />,
    },
    {
      key: 'owner',
      label: 'Managed by',
      render: (node) => {
        if (node.kind === 'unscoped') return <Box component="span" sx={{ color: CK.muted }}>—</Box>;
        const chip = ownershipChip(node.kind === 'scope' ? node.scope : node.pool);
        return (
          <Tooltip title={chip.hint}>
            <Chip size="small" label={chip.label} sx={statusChipSx(chip.intent)} />
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (node) =>
        node.kind === 'pool' ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/securityhub/ipam/pools/${encodeURIComponent(node.pool.poolId)}`)}
            sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: IPAM_COLOR }}
          >
            Open
          </Button>
        ) : null,
    },
  ], [navigate, scopes.length]);

  const header = (
    <PortalPageHeader
      icon={<LanIcon sx={{ fontSize: 24 }} />}
      title="IPAM pools"
      color={IPAM_COLOR}
      subtitle={IPAM_POOLS_SUBTITLE}
      action={(
        <Box sx={actionRowSx}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={refreshing}
            sx={{ fontFamily: FONT_BODY, textTransform: 'none', borderColor: CK.border, color: CK.navy }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          {/* A disabled MUI Button emits no pointer events, so the Tooltip needs
              a wrapper to hang off or the reason never reaches the screen. */}
          <Tooltip title={createDisabledReason || 'Carve a new pool out of a scope or a parent pool'}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!!createDisabledReason}
                onClick={() => setCreateOpen(true)}
                sx={{
                  fontFamily: FONT_BODY,
                  textTransform: 'none',
                  backgroundColor: IPAM_COLOR,
                  '&:hover': { backgroundColor: '#31434B' },
                  '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
                }}
              >
                Create pool
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    />
  );

  const createDialog = (
    <CreatePoolDialog
      open={createOpen}
      onClose={() => setCreateOpen(false)}
      onCreated={() => { setCreateOpen(false); load(); }}
      scopes={scopes}
      pools={pools || []}
      operatingRegions={parseRegions(config?.operatingRegions)}
      homeRegion={config?.homeRegion}
    />
  );

  // MUST PRECEDE EVERY BRANCH THAT STATES WHAT THE CUSTOMER'S SETUP IS. A
  // rejected GET /config leaves `config` null, and the branches below read a
  // null config as an answer. Keyed on the READ OUTCOME as well as on `error` —
  // the same guard IpamAllocations.js, IpamAccounts.js and IpamPublicIPs.js use
  // — because an `error`-only test holds just while every rejection path
  // happens to carry a message, and the branch that inherits the case where one
  // does not is a claim about a customer's IPAM.
  if (error || (configRead === 'failed' && !config)) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error || IPAM_POOLS_CONFIG_UNREAD_ERROR} onRetry={load} />
        </motion.div>
      </motion.div>
    );
  }

  if (pools === null) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading the pool hierarchy…" color={IPAM_COLOR} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // ── NOT LIVE: DECIDED BEFORE THE TREE IS LOOKED AT ─────────────────────────
  //
  // THIS BRANCH USED TO LIVE INSIDE `if (pools.length === 0)`, WHICH IS WHY A
  // TORN-DOWN TENANT SAW TWO POOLS. The ledger still answered with stale rows
  // from a dead test cycle, the tree was therefore not empty, and the not-live
  // question was never asked: the rows rendered under the ordinary chrome,
  // "Creating", "Managed by Prism", indistinguishable from a running IPAM.
  //
  // The rows are not evidence. Only the config row can answer "does an IPAM
  // exist", so it answers first — and when the answer is no, THE ROWS ARE NOT
  // DRAWN AT ALL, however many of them the API returned. This is the second line
  // of defence and it does not depend on the backend stopping at the source: a
  // stale row that reaches the browser must not be presented as current.
  //
  // ── AND "NOT LIVE" IS NOT "THERE IS NO IPAM" ───────────────────────────────
  //
  // The status answers whether PRISM is running this capability. It does not
  // answer whether an IPAM exists: a clean teardown of an ADOPTED IPAM settles
  // `disabled` over an IPAM that is deliberately never deleted and goes on
  // metering, and `failed` is settled by ipamPhaseRollup whenever the host cell
  // is not `enabled` — which includes CreateIpam SUCCEEDING and a later host step
  // failing, with the id already persisted. ipamResidualIpamCard reads ipamId and
  // ipamAdopted, which no screen in this capability read, and states the truth
  // WITH THE REMEDY. It answers null whenever there is no such evidence, and the
  // original card stands.
  if (liveness === NOT_LIVE) {
    const meta = residualCard || notLiveMeta(config?.status);
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<LanIcon sx={{ fontSize: 28 }} />}
              title={meta.title}
              description={meta.description}
              color={IPAM_COLOR}
              action={(
                <Button
                  variant="contained"
                  onClick={() => navigate('/securityhub/ipam')}
                  sx={{
                    fontFamily: FONT_BODY,
                    textTransform: 'none',
                    backgroundColor: IPAM_COLOR,
                    '&:hover': { backgroundColor: '#31434B' },
                  }}
                >
                  Go to VPC IPAM
                </Button>
              )}
            />
          </Paper>
        </motion.div>
        {createDialog}
      </motion.div>
    );
  }

  // ── THE THIRD ANSWER: PRISM COULD NOT FIND OUT, AND SAYS SO ────────────────
  //
  // Not the not-live card with softer wording — a different claim. With nothing
  // in the tree there is also no "No pools yet" to print: a zero is a statement
  // about AWS, and the read that would license one is the read that did not
  // land. The way out is the read itself, so the action is a retry.
  if (liveness === UNREAD && pools.length === 0) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<LanIcon sx={{ fontSize: 28 }} />}
              title={IPAM_POOLS_LIVENESS_UNREAD_TITLE}
              description={IPAM_POOLS_LIVENESS_UNREAD_BODY}
              color={IPAM_COLOR}
              action={(
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={refreshing}
                  sx={{
                    fontFamily: FONT_BODY,
                    textTransform: 'none',
                    backgroundColor: IPAM_COLOR,
                    '&:hover': { backgroundColor: '#31434B' },
                  }}
                >
                  {refreshing ? 'Refreshing…' : 'Re-read the configuration'}
                </Button>
              )}
            />
          </Paper>
        </motion.div>
        {createDialog}
      </motion.div>
    );
  }

  // THE LIVE-BUT-EMPTY TREE. Unchanged, and verified live: no cold start — a
  // pool is never discovered, so there is no sweep to wait for — but
  // `refreshPoolLedger` re-reads pools from AWS on every refresh, so an empty
  // tree is a genuine zero OR a pool read that did not land, and the way out is
  // to run the read again.
  if (pools.length === 0) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{header}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<LanIcon sx={{ fontSize: 28 }} />}
              title="No pools yet"
              description={IPAM_POOLS_EMPTY_DESCRIPTION}
              color={IPAM_COLOR}
              action={(
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={refreshing}
                  sx={{
                    fontFamily: FONT_BODY,
                    textTransform: 'none',
                    backgroundColor: IPAM_COLOR,
                    '&:hover': { backgroundColor: '#31434B' },
                  }}
                >
                  {refreshing ? 'Refreshing…' : 'Re-read pools from AWS'}
                </Button>
              )}
            />
          </Paper>
        </motion.div>
        {createDialog}
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{header}</motion.div>

      {/* THE DEGRADATION NOTE, ON THE PAGE THE ROWS ARE ON. `config.note` is one
          slot with several writers, and the unattributed ones are the DEGRADATION
          channel — the loudest of them being "AWS no longer reports IPAM %s …
          Nothing is being discovered and no pool can be allocated from". It was
          rendered as severity="info" on the dashboard and NOWHERE ELSE, so the
          page still drawing the pools that sentence has just declared unusable
          never said a word about it. */}
      {noteSeverity && (
        <motion.div variants={sectionVariants}>
          <Alert severity={noteSeverity} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {noteTitle && (
              <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>{noteTitle}</AlertTitle>
            )}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>{config.note}</Typography>
          </Alert>
        </motion.div>
      )}

      {/* LIVENESS UNKNOWN, ROWS PRESENT. The tree is still drawn — hiding pools
          over a field Prism failed to read would be its own false negative, and
          these rows may be a live customer's real address space — but it is
          drawn UNDER a statement that Prism has not confirmed the IPAM behind
          them exists. That is the difference between showing data and vouching
          for it, and it is the whole reason UNREAD is not folded into either of
          the other two answers. */}
      {liveness === UNREAD && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {IPAM_POOLS_UNVERIFIED_TREE_TITLE}
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              {IPAM_POOLS_UNVERIFIED_TREE_BODY}
            </Typography>
          </Alert>
        </motion.div>
      )}

      {/* An account that has left the organization is stated ABOVE the tree, not
          left to a chip somebody has to notice. Nothing about these pools can be
          retried, so an operator scanning for "what is broken" has to find them
          without hunting. */}
      {isolatedCount > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {isolatedCount} {isolatedCount === 1 ? 'pool belongs' : 'pools belong'} to an account
              that has left the organization
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Prism has no way into an account outside the organization — there is no grant to
              attach and no session to open — so retrying anything on{' '}
              {isolatedCount === 1 ? 'this pool' : 'these pools'} cannot work. They may still exist
              and still bill inside that account. Bringing the account back into the organization is
              the only path.
            </Typography>
          </Alert>
        </motion.div>
      )}

      {/* Same split as the Unscoped root row itself: a lookup that missed while
          other pools DID place is a different fact from a tree with no scopes
          under it at all, and the second one is a great deal more likely to be
          residue than address space. */}
      {unscopedCount > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {unscopedAlert.title}
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              {unscopedAlert.body}
            </Typography>
          </Alert>
        </motion.div>
      )}

      {missingCount > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              AWS stopped returning {missingCount} {missingCount === 1 ? 'pool' : 'pools'}
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Prism has not deleted {missingCount === 1 ? 'it' : 'them'}. Either{' '}
              {missingCount === 1 ? 'it is' : 'they are'} already gone, which is harmless, or{' '}
              {missingCount === 1 ? 'it is an orphan' : 'they are orphans'} still occupying quota —
              badged <b>Not seen in AWS</b> below.
            </Typography>
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <ResourceTree
            nodes={nodes}
            columns={columns}
            getId={(node) => node.nodeId}
            getLabel={(node) => {
              if (node.kind === 'scope') return node.scope.description || node.scope.scopeId;
              if (node.kind === 'pool') return node.pool.description || node.pool.poolId;
              return 'Unscoped pools';
            }}
            expanded={expanded}
            onToggle={handleToggle}
            // AWS's own pool-depth ceiling. Deeper subtrees are DECLARED by
            // <ResourceTree> as "n deeper levels not shown", never dropped.
            maxDepth={10}
            minWidth={980}
          />
        </Paper>
      </motion.div>

      {createDialog}
    </motion.div>
  );
}

// ── Create pool ─────────────────────────────────────────────────────────────
//
// THE PUBLIC SCOPE IS REFUSED HERE, WITH THE REASON, RATHER THAN AT THE API.
//
// The backend refuses it too — a public-scope pool makes DeleteIpam --cascade
// fail outright, and cascade is the only proven way to stop the meter once a
// pool cannot be drained, so one operator API call could leave an IPAM
// permanently undeletable and permanently billing. But a 400 that arrives after
// the form is filled in teaches the operator nothing except that Prism is
// fussy. The option is visibly disabled with the reason attached, so the limit
// is understood before any effort is spent on it.
function CreatePoolDialog({ open, onClose, onCreated, scopes, pools, operatingRegions, homeRegion }) {
  const privateScopes = useMemo(() => scopes.filter((s) => !isPublicScope(s)), [scopes]);
  const publicScopes = useMemo(() => scopes.filter(isPublicScope), [scopes]);
  // THE SAME TREATMENT THE PUBLIC SCOPE ALREADY GETS, FOR THE SAME REASON, given
  // to a scope AWS has stopped returning: listed, greyed, with the reason on it.
  // See ipamScopeCreateBlock — dead scopes are shown because an orphan has to be
  // nameable, and are not offered because the create cannot work.
  const creatableScopes = useMemo(
    () => privateScopes.filter((s) => !ipamScopeCreateBlock(s)),
    [privateScopes]
  );
  const blockedScopes = useMemo(
    () => privateScopes
      .map((s) => ({ scope: s, block: ipamScopeCreateBlock(s) }))
      .filter((e) => e.block),
    [privateScopes]
  );

  const [scopeId, setScopeId] = useState('');
  const [sourcePoolId, setSourcePoolId] = useState('');
  const [addressFamily, setAddressFamily] = useState('ipv4');
  const [locale, setLocale] = useState('');
  const [description, setDescription] = useState('');
  const [minNetmask, setMinNetmask] = useState('');
  const [maxNetmask, setMaxNetmask] = useState('');
  const [defaultNetmask, setDefaultNetmask] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // The default landing scope, as a PRIMITIVE. The reset effect below keys on
  // this rather than on `privateScopes`, whose array identity changes on every
  // reload — an effect keyed on the array would wipe a half-filled form the
  // moment anything refreshed the scope list underneath it.
  //
  // IT IS DRAWN FROM `creatableScopes`, AND THAT IS THE WHOLE OF THE LIVE BUG.
  // `isDefault` is true of the DEAD default scopes too — they were the default
  // of the IPAM that used to exist — so a list ordered with a previous
  // generation's row first pre-selected a scope the create refuses. With nothing
  // creatable the value stays '', which leaves the select empty and `canSubmit`
  // false, and the alert above it says why: an empty field the operator can see
  // beats a filled one that cannot work.
  const defaultScopeId = useMemo(
    () => creatableScopes.find((s) => s.isDefault)?.scopeId || creatableScopes[0]?.scopeId || '',
    [creatableScopes]
  );

  // Reset on every open, so a refusal from a previous attempt never sits behind
  // a fresh form and a half-typed CIDR never survives a cancel.
  useEffect(() => {
    if (!open) return;
    setScopeId(defaultScopeId);
    setSourcePoolId('');
    setAddressFamily('ipv4');
    setLocale('');
    setDescription('');
    setMinNetmask('');
    setMaxNetmask('');
    setDefaultNetmask('');
    setSubmitError(null);
    setSubmitting(false);
  }, [open, defaultScopeId]);

  // Only pools in the chosen scope can be a parent — AWS requires a child pool to
  // live in its parent's scope, and so does Prism's own fence.
  const nestableParents = useMemo(
    () => pools.filter((p) => p.scopeId === scopeId && !p.unscoped),
    [pools, scopeId]
  );
  const sourcePool = useMemo(
    () => nestableParents.find((p) => p.poolId === sourcePoolId) || null,
    [nestableParents, sourcePoolId]
  );

  // A nested pool INHERITS its parent's address family and locale — AWS refuses
  // any other combination — so both fields lock rather than lying about a choice.
  const effectiveFamily = sourcePool ? sourcePool.addressFamily : addressFamily;
  const effectiveLocale = sourcePool ? sourcePool.locale : locale;

  const localeOptions = useMemo(() => {
    const set = new Set(operatingRegions);
    if (homeRegion) set.add(homeRegion);
    return [...set].sort();
  }, [operatingRegions, homeRegion]);

  const numberOrUndefined = (raw) => {
    const trimmed = String(raw).trim();
    if (trimmed === '') return undefined;
    const n = Number(trimmed);
    return Number.isFinite(n) ? Math.trunc(n) : undefined;
  };

  // THE SELECTED SCOPE IS RE-CHECKED, NOT TRUSTED. A disabled MenuItem cannot be
  // picked, but the scope list is re-read underneath this dialog: a scope that
  // was live when the form opened can be marked missing by a refresh while it is
  // still on screen. The submit fails closed on the current fact rather than on
  // the one that was true when the value was chosen.
  const selectedBlock = useMemo(
    () => {
      const picked = scopes.find((s) => s.scopeId === scopeId);
      return picked ? ipamScopeCreateBlock(picked) : null;
    },
    [scopes, scopeId]
  );

  const canSubmit = Boolean(scopeId) && !selectedBlock && Boolean(effectiveFamily) && !submitting;

  // AND WHY IT IS NOT SUBMITTABLE, said out loud. `canSubmit` reads false with a
  // completely empty Scope select on a tenant with no IPAM, which is a greyed
  // button with nothing on screen explaining it.
  const disabledReason = (() => {
    if (canSubmit) return '';
    if (selectedBlock && !submitting) return selectedBlock.reason;
    return ipamCreatePoolDisabledReason({
      privateScopeCount: creatableScopes.length,
      deadPrivateScopeCount: privateScopes.length - creatableScopes.length,
      submitting,
    });
  })();

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Netmask lengths are POINTERS server-side, so an omitted key means "let
      // AWS decide" while a zero would mean "set it to zero". Undefined values
      // are dropped by JSON.stringify, which is exactly the wanted behaviour.
      await createIpamPool({
        scopeId,
        sourceIpamPoolId: sourcePoolId || undefined,
        description: description.trim(),
        addressFamily: sourcePool ? undefined : addressFamily,
        locale: sourcePool ? undefined : (locale || undefined),
        allocationMinNetmaskLength: numberOrUndefined(minNetmask),
        allocationMaxNetmaskLength: numberOrUndefined(maxNetmask),
        allocationDefaultNetmaskLength: numberOrUndefined(defaultNetmask),
      });
      onCreated();
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} {...dialogProps} maxWidth="md">
      <DialogTitle sx={dialogTitleSx}>Create pool</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {submitError}
          </Alert>
        )}

        {/* THE COUNT IS OF CREATABLE SCOPES, NOT OF ROWS HELD. A customer with
            two private scopes from an IPAM that no longer exists has no live
            scope to create into, and "there is no private scope" would be a
            false sentence sitting directly above two of them. */}
        {creatableScopes.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            {ipamCreatePoolDisabledReason({
              privateScopeCount: 0,
              deadPrivateScopeCount: privateScopes.length,
            })}
          </Alert>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
          <InputLabel sx={{ fontFamily: FONT_BODY }}>Scope</InputLabel>
          <Select
            label="Scope"
            value={scopeId}
            onChange={(e) => { setScopeId(e.target.value); setSourcePoolId(''); }}
            sx={{ fontFamily: FONT_BODY }}
          >
            {/* THE LIVE ONES FIRST, AND ONLY THESE ARE SELECTABLE. Ordering is
                part of the fix: the wire order put a dead scope at the top of
                the list, and the top of the list is what gets pre-selected. */}
            {creatableScopes.map((s) => (
              <MenuItem key={s.scopeId} value={s.scopeId} sx={{ fontFamily: FONT_BODY }}>
                {(s.description || 'Private scope')} — {s.scopeId}{s.isDefault ? ' (default)' : ''}
              </MenuItem>
            ))}
            {/* Then the ones AWS has stopped returning, or reports as gone —
                LISTED, because an orphan has to be nameable, and DISABLED, with
                the reason in the option itself. */}
            {blockedScopes.map(({ scope: s, block }) => (
              <MenuItem key={s.scopeId} value={s.scopeId} disabled sx={{ fontFamily: FONT_BODY }}>
                {(s.description || 'Private scope')} — {s.scopeId} ({block.label})
              </MenuItem>
            ))}
            {/* Rendered and DISABLED rather than omitted. An option that simply
                is not there reads as a Prism bug; an option that is there,
                greyed, and says why reads as a decision. */}
            {publicScopes.map((s) => (
              <MenuItem key={s.scopeId} value={s.scopeId} disabled sx={{ fontFamily: FONT_BODY }}>
                {(s.description || 'Public scope')} — {s.scopeId} (public — not supported)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {blockedScopes.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              {IPAM_SCOPE_DEAD_OPTIONS_TITLE}
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              {IPAM_SCOPE_DEAD_OPTIONS_BODY}
            </Typography>
          </Alert>
        )}

        {publicScopes.length > 0 && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: '8px', fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              Prism does not create pools in the public scope
            </AlertTitle>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem' }}>
              Deleting an IPAM with the cascade option is the only way to stop the meter when a pool
              cannot be drained by hand — and AWS refuses that delete outright while <b>any</b> pool
              exists in the public scope. One public-scope pool can therefore leave the IPAM
              permanently undeletable and permanently billing, so Prism will not create one. Use the
              private scope.
            </Typography>
          </Alert>
        )}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel sx={{ fontFamily: FONT_BODY }}>Carve out of (optional)</InputLabel>
          <Select
            label="Carve out of (optional)"
            value={sourcePoolId}
            onChange={(e) => setSourcePoolId(e.target.value)}
            sx={{ fontFamily: FONT_BODY }}
          >
            <MenuItem value="" sx={{ fontFamily: FONT_BODY }}>
              None — a top-level pool in this scope
            </MenuItem>
            {nestableParents.map((p) => (
              <MenuItem key={p.poolId} value={p.poolId} sx={{ fontFamily: FONT_BODY }}>
                {(p.description || 'Pool')} — {p.poolId} ({p.addressFamily}
                {p.locale ? `, ${p.locale}` : ', no locale'})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ flex: '1 1 200px' }} disabled={Boolean(sourcePool)}>
            <InputLabel sx={{ fontFamily: FONT_BODY }}>Address family</InputLabel>
            <Select
              label="Address family"
              value={effectiveFamily || ''}
              onChange={(e) => setAddressFamily(e.target.value)}
              sx={{ fontFamily: FONT_BODY }}
            >
              <MenuItem value="ipv4" sx={{ fontFamily: FONT_BODY }}>ipv4</MenuItem>
              <MenuItem value="ipv6" sx={{ fontFamily: FONT_BODY }}>ipv6</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: '1 1 200px' }} disabled={Boolean(sourcePool)}>
            <InputLabel sx={{ fontFamily: FONT_BODY }}>Locale</InputLabel>
            <Select
              label="Locale"
              value={effectiveLocale || ''}
              onChange={(e) => setLocale(e.target.value)}
              sx={{ fontFamily: FONT_BODY }}
            >
              <MenuItem value="" sx={{ fontFamily: FONT_BODY }}>None</MenuItem>
              {localeOptions.map((r) => (
                <MenuItem key={r} value={r} sx={{ fontFamily: FONT_BODY }}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {sourcePool ? (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.muted, mb: 2 }}>
            A nested pool must match its parent’s address family and locale, so both are taken from{' '}
            <Box component="span" sx={{ fontFamily: FONT_MONO }}>{sourcePool.poolId}</Box>. AWS
            refuses any other combination.
          </Typography>
        ) : (
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.muted, mb: 2 }}>
            <b>The locale cannot be changed later.</b> A pool with no locale can hold CIDRs but AWS
            will not allocate them to a VPC in any region. Only regions this IPAM operates in are
            offered.
          </Typography>
        )}

        <TextField
          fullWidth
          size="small"
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mb: 2, '& .MuiInputBase-input': { fontFamily: FONT_BODY } }}
          InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="Min netmask"
            value={minNetmask}
            onChange={(e) => setMinNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
          <TextField
            size="small"
            type="number"
            label="Max netmask"
            value={maxNetmask}
            onChange={(e) => setMaxNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
          <TextField
            size="small"
            type="number"
            label="Default netmask"
            value={defaultNetmask}
            onChange={(e) => setDefaultNetmask(e.target.value)}
            sx={{ flex: '1 1 140px', '& .MuiInputBase-input': { fontFamily: FONT_MONO } }}
            InputLabelProps={{ sx: { fontFamily: FONT_BODY } }}
          />
        </Box>

        <Box sx={insetPanelSx}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body }}>
            Prism will not set <b>auto-import</b> on a pool it creates: auto-import pulls in CIDRs
            that break the pool’s own rules, keeps only the largest of any that overlap, and{' '}
            <b>breaks ties at random</b> — a change to your address space no audit record could
            explain. It is shown read-only on pools that already have it. BYOIP pools (publicly
            advertisable, or a public IP source of <Box component="span" sx={{ fontFamily: FONT_MONO }}>byoip</Box>)
            and pools tied to an AWS service are a later phase.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ fontFamily: FONT_BODY, textTransform: 'none', color: CK.body }}>
          Cancel
        </Button>
        <Tooltip title={disabledReason || 'Create this pool'}>
          <span>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!canSubmit}
              sx={{
                fontFamily: FONT_BODY,
                textTransform: 'none',
                backgroundColor: IPAM_COLOR,
                '&:hover': { backgroundColor: '#31434B' },
                '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.26)' },
              }}
            >
              {submitting ? 'Creating…' : 'Create pool'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
